import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { useLang } from "@/context/LangContext";

export const HERO_POSTER = "/media/minang-landscape-poster.jpg";
const VIDEO = "/media/minang-landscape-hero.mp4";

export const HeroVideo = ({ reducedMotion }) => {
  const { t } = useLang();
  const videoRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [visible, setVisible] = useState(!document.hidden);
  const [choice, setChoice] = useState(null);
  const [source, setSource] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [frame, setFrame] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [failed, setFailed] = useState(false);
  const wantsPlay = choice ?? !reducedMotion;
  const allowed = wantsPlay && inView && visible && !blocked && !failed;

  useEffect(() => {
    const node = videoRef.current;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0 });
    const visibility = () => setVisible(!document.hidden);
    observer.observe(node);
    document.addEventListener("visibilitychange", visibility);
    return () => { observer.disconnect(); document.removeEventListener("visibilitychange", visibility); node.pause(); };
  }, []);

  useEffect(() => { setChoice((current) => current === false ? false : null); }, [reducedMotion]);

  const handlePlayError = (error) => {
    // Aborts are expected when the visitor scrolls away or pauses a pending play.
    if (error.name === "AbortError") return;
    setPlaying(false);
    setFrame(false);
    if (error.name === "NotAllowedError") setBlocked(true);
    else setFailed(true);
  };

  useEffect(() => {
    const node = videoRef.current;
    if (!allowed) { node.pause(); return undefined; }
    if (!source) { setSource(true); return undefined; }
    node.muted = true;
    node.play().catch(handlePlayError);
    return () => node.pause();
  }, [allowed, source]);

  const toggle = () => {
    const node = videoRef.current;
    if (playing || (wantsPlay && source && !blocked && !failed)) {
      setChoice(false);
      node.pause();
      return;
    }
    setChoice(true);
    setBlocked(false);
    setFailed(false);
    if (!source) { node.src = VIDEO; setSource(true); }
    else if (failed) node.load();
    node.muted = true;
    // Keep this call in the click handler so Safari can use the user gesture.
    node.play().catch(handlePlayError);
  };
  const canPause = playing || (wantsPlay && source && !blocked && !failed);

  return <>
    <motion.video ref={videoRef} id="hero-landscape-video" data-testid="hero-background-video" data-playing={playing} aria-hidden="true" tabIndex={-1}
      src={source ? VIDEO : undefined} poster={HERO_POSTER} muted loop playsInline autoPlay={allowed} preload="none" disablePictureInPicture
      initial={false} animate={{ opacity: frame && !failed && !blocked ? 1 : 0 }} transition={{ duration: reducedMotion ? 0 : 0.3 }}
      className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      onPlaying={() => { setPlaying(true); setFrame(true); setBlocked(false); }} onPause={() => setPlaying(false)}
      onError={() => { setFailed(true); setFrame(false); setPlaying(false); }} />
    <div className="hero-media-controls relative z-10 flex w-full flex-wrap items-center justify-between gap-3 px-5 pb-5 sm:px-8">
      <a data-testid="hero-footage-credit" href="https://www.pexels.com/video/pesona-alam-minang-kabau-20540066/" target="_blank" rel="noreferrer" className="inline-flex min-h-11 min-w-0 items-center text-xs text-white/85 underline decoration-white/30 underline-offset-4 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-300">{t("hero_footage_credit")}</a>
      <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
        {(failed || blocked) && <span data-testid="hero-video-status" role="status" className="max-w-56 text-xs leading-relaxed text-white">{t(failed ? "hero_video_unavailable" : "hero_video_blocked")}</span>}
        <button type="button" data-testid="hero-video-toggle-button" aria-label={t(canPause ? "hero_video_pause" : "hero_video_play")} aria-controls="hero-landscape-video" onClick={toggle} className="flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-full border border-white/40 bg-[#022C22]/80 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#064E3B] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-300">
          {canPause ? <Pause className="h-4 w-4 shrink-0" aria-hidden="true" /> : <Play className="h-4 w-4 shrink-0" aria-hidden="true" />}
          <span aria-hidden="true" className="inline-grid min-w-0"><span className={`col-start-1 row-start-1 ${canPause ? "" : "invisible"}`}>{t("hero_video_pause")}</span><span className={`col-start-1 row-start-1 ${canPause ? "invisible" : ""}`}>{t("hero_video_play")}</span></span>
        </button>
      </div>
    </div>
  </>;
};
